'use client';

import { useState, useRef, useCallback, type PointerEvent as ReactPointerEvent } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogTrigger,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Loader2, Camera, X, ImagePlus, CornerDownLeft, Paperclip } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';
import {
  createIssueReport,
  IssueReportRequestError,
  type IssueReportAuth,
  type IssueReportSource,
} from '@/lib/api/issue-reports';
import { buildSafeIssueDiagnostic } from '@/lib/issue-report-diagnostic';
import { isModalIssueReportTarget } from './modal-issue-report-button';

export const MAX_DESCRIPTION_LENGTH = 2000;
export const MAX_SCREENSHOTS = 3;
/** 첨부 문서는 종류를 보지 않고 크기만 본다(사람 10-07). 서버 IssueReportService.MAX_FILE_SIZE 와 같은 값. */
export const MAX_FILE_MB = 20;

function formatSize(bytes: number): string {
  if (bytes < 1024 * 1024) return `${Math.max(1, Math.round(bytes / 1024))}KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)}MB`;
}

const SOURCE_LABEL: Record<IssueReportSource, string> = {
  DASHBOARD: '대시보드',
  AI_EDITOR_MEMBER: '문서 작성',
  GUEST_APPLY: '지원서 작성',
};

/** 접수 뒤 안내. 로그인 세션 없는 신청자에게 `/my-issues` 이동을 요구하지 않는다. */
const SUCCESS_BY_AUTH: Record<IssueReportAuth['kind'], string> = {
  member: '접수했습니다. 진행 상황은 「내 신고」에서 확인할 수 있습니다.',
  'applicant-link': '접수했습니다. 답변은 지원할 때 등록한 이메일로 안내합니다.',
};

interface ScreenshotEntry {
  blob: Blob;
  url: string;
  label: string; // "현재 화면" | "붙여넣기" | "사진"
}

export interface IssueReportDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** 없으면 접수할 수 없다 — 버튼 쪽에서 disabled 로 막지만 여기서도 한 번 더 본다. */
  auth: IssueReportAuth | null;
  sourceType: IssueReportSource;
  /** 회원 작성 화면(`/ai-editor`)에서만. 서버가 이걸로 task 문맥을 찾는다. */
  sourceAccessId?: string;
  /** 대시보드 신고의 페이지 주소. 없으면 지금 주소다. */
  currentUrl?: string;
  /** 도움말을 보다 신고하면 그 문서 id. 진단 정보에 실려 작성 화면·신청자 신고에서도 남는다(점검 N-S17). */
  helpDocId?: string;
}

function isIssueReportDialog(el: Element | null): boolean {
  return el instanceof HTMLElement && el.dataset.issueReportDialog === 'true';
}

/**
 * 문제 신고 다이얼로그 — open 상태, form, 첨부, 제출만 담당한다. 화면마다 `IssueReportProvider` 가 하나 두고,
 * 사이드바·편집기 머리의 단추와 모든 모달 왼쪽 아래 아이콘이 그것으로 연다(10-01 데모 R9).
 *
 * - 열 때 화면을 자동 캡처하지 않는다. 신청서 화면에는 개인정보가 있을 수 있다 —
 *   사용자가 「현재 화면 첨부」를 눌렀을 때만 캡처하고, 보내기 전에 미리보기로 확인한다.
 * - 진단 정보는 allowlist 만 보낸다 (`buildSafeIssueDiagnostic`). console log 본문은 없다.
 * - 신고자 이름·이메일·taskId 를 form 에 넣지 않는다. 서버가 인증 수단으로 확정한다.
 * 계획: docs/plans/2026-08-24/guest-apply-임시계정-문제신고-연결-계획.md §4.3·§9.1
 */
export function IssueReportDialog({
  open,
  onOpenChange,
  auth,
  sourceType,
  sourceAccessId,
  currentUrl,
  helpDocId,
}: IssueReportDialogProps) {
  const { toast } = useToast();

  const [description, setDescription] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [screenshots, setScreenshots] = useState<ScreenshotEntry[]>([]);
  const [capturing, setCapturing] = useState(false);
  /** 접수 실패 안내. 창을 닫지 않고 그 자리에서 보여 준다. */
  const [failure, setFailure] = useState<string | null>(null);
  const [captureNote, setCaptureNote] = useState<string | null>(null);

  const fileInputRef = useRef<HTMLInputElement | null>(null);
  /** 화면·사진 밖의 문서. 종류는 묻지 않는다. */
  const [files, setFiles] = useState<File[]>([]);
  const docInputRef = useRef<HTMLInputElement | null>(null);

  const resetForm = useCallback(() => {
    setDescription('');
    setFailure(null);
    setCaptureNote(null);
    setFiles([]);
    setScreenshots((prev) => {
      prev.forEach((s) => URL.revokeObjectURL(s.url));
      return [];
    });
  }, []);

  // 창을 머리로 끌어 옮긴다 - 뒤 화면을 보면서 신고를 쓰려고(데모 피드백 F13). 닫았다 열면 가운데로 돌아온다.
  const [offset, setOffset] = useState({ x: 0, y: 0 });
  const contentRef = useRef<HTMLDivElement | null>(null);
  const startDrag = useCallback((event: ReactPointerEvent<HTMLDivElement>) => {
    if (event.button !== 0 || (event.target as HTMLElement).closest('button, a, input, textarea')) return;
    const box = contentRef.current?.getBoundingClientRect();
    if (!box) return;
    event.preventDefault();
    const start = { x: event.clientX, y: event.clientY };
    const origin = offset;
    // 창이 화면 밖으로 나가지 않게 - 가운데 기준으로 움직일 수 있는 폭만큼만.
    const maxX = Math.max(0, (window.innerWidth - box.width) / 2);
    const maxY = Math.max(0, (window.innerHeight - box.height) / 2);
    const clamp = (value: number, max: number) => Math.min(max, Math.max(-max, value));
    const move = (e: PointerEvent) => setOffset({
      x: clamp(origin.x + e.clientX - start.x, maxX),
      y: clamp(origin.y + e.clientY - start.y, maxY),
    });
    const end = () => {
      window.removeEventListener('pointermove', move);
      window.removeEventListener('pointerup', end);
    };
    window.addEventListener('pointermove', move);
    window.addEventListener('pointerup', end);
  }, [offset]);

  const handleOpenChange = useCallback(
    (nextOpen: boolean) => {
      if (!nextOpen) resetForm();
      setOffset({ x: 0, y: 0 });
      onOpenChange(nextOpen);
    },
    [onOpenChange, resetForm],
  );

  const addScreenshot = useCallback((blob: Blob, label: string) => {
    setScreenshots((prev) => {
      if (prev.length >= MAX_SCREENSHOTS) return prev;
      return [...prev, { blob, url: URL.createObjectURL(blob), label }];
    });
  }, []);

  // --- 현재 화면 첨부: 사용자가 눌렀을 때만 ---
  const captureScreen = useCallback(async () => {
    if (screenshots.length >= MAX_SCREENSHOTS) return;
    setCapturing(true);
    setCaptureNote(null);
    try {
      const html2canvas = (await import('html2canvas')).default;
      const canvas = await html2canvas(document.body, {
        scale: Math.min(window.devicePixelRatio || 1, 2),
        logging: false,
        useCORS: true,
        // 열린 신고 창 자체와 그 바로 앞의 어두운 덮개는 캡처에서 뺀다 — 문제가 난 화면이 밝게 보여야 한다.
        // 덮개를 남기면 화면 전체가 검게 덮여 찍혔다(2026-09-29 신고).
        ignoreElements: (el) => isIssueReportDialog(el) || isIssueReportDialog(el.nextElementSibling)
          || isModalIssueReportTarget(el),
        onclone: (clonedDoc: Document) => {
          // Firefox 향상된 추적 방지가 차단하는 폰트(한컴돋움 등)를
          // 시스템 폰트로 대체하여 canvas height 0 에러 방지
          const style = clonedDoc.createElement('style');
          style.textContent = ['한컴돋움', '한컴바탕', '한컴고딕']
            .map((f) => `@font-face { font-family: "${f}"; src: local("Dotum"), local("돋움"), local(sans-serif); }`)
            .join('\n');
          clonedDoc.head.appendChild(style);
        },
      });
      const blob = await new Promise<Blob | null>((resolve) =>
        canvas.toBlob((b) => resolve(b), 'image/png', 0.8),
      );
      if (blob) {
        addScreenshot(blob, '현재 화면');
      } else {
        setCaptureNote('화면을 담지 못했습니다. 사진을 추가하거나 Ctrl+V 로 붙여넣어주세요.');
      }
    } catch (e) {
      console.warn('화면 캡처 실패:', e);
      setCaptureNote('화면을 담지 못했습니다. 사진을 추가하거나 Ctrl+V 로 붙여넣어주세요.');
    } finally {
      setCapturing(false);
    }
  }, [addScreenshot, screenshots.length]);

  // --- 스크린샷: 클립보드 붙여넣기 ---
  const handlePaste = useCallback(
    (e: React.ClipboardEvent) => {
      if (screenshots.length >= MAX_SCREENSHOTS) return;
      const items = e.clipboardData?.items;
      if (!items) return;
      for (const item of Array.from(items)) {
        if (item.type.startsWith('image/')) {
          e.preventDefault();
          const blob = item.getAsFile();
          if (blob) addScreenshot(blob, '붙여넣기');
          break;
        }
      }
    },
    [addScreenshot, screenshots.length],
  );

  const removeScreenshot = useCallback((index: number) => {
    setScreenshots((prev) => {
      const removed = prev[index];
      if (removed) URL.revokeObjectURL(removed.url);
      return prev.filter((_, i) => i !== index);
    });
  }, []);

  // --- 사진 업로드 (모바일 카메라/갤러리) ---
  const handleFileSelect = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const files = e.target.files;
      if (!files) return;
      for (const file of Array.from(files)) {
        if (!file.type.startsWith('image/')) continue;
        addScreenshot(file, '사진');
      }
      // 같은 파일 재선택 허용
      e.target.value = '';
    },
    [addScreenshot],
  );

  // --- 문서 첨부: 아무 파일이나, 크기만 본다 ---
  const handleDocSelect = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const picked = Array.from(e.target.files ?? []);
    const tooBig = picked.filter((f) => f.size > MAX_FILE_MB * 1024 * 1024);
    const ok = picked.filter((f) => f.size <= MAX_FILE_MB * 1024 * 1024);
    if (ok.length) setFiles((prev) => [...prev, ...ok]);
    setCaptureNote(tooBig.length
      ? `${tooBig.map((f) => `「${f.name}」`).join(', ')}은 ${MAX_FILE_MB}MB 를 넘어 붙이지 못했습니다.`
      : null);
    e.target.value = '';
  }, []);

  const removeFile = useCallback((index: number) => {
    setFiles((prev) => prev.filter((_, i) => i !== index));
  }, []);

  // --- 제출 ---
  const hasContent = description.trim().length > 0 || screenshots.length > 0 || files.length > 0;
  const canSubmit = hasContent && auth !== null && !submitting;

  const handleSubmit = useCallback(async () => {
    if (!auth) {
      setFailure('신고자 계정을 확인하지 못해 접수할 수 없습니다. 작성 주소와 화면을 보관한 뒤 담당자에게 알려주세요.');
      return;
    }
    if (!hasContent) {
      setFailure('내용을 입력하거나 화면·파일을 첨부해주세요.');
      return;
    }

    setSubmitting(true);
    setFailure(null);
    try {
      const formData = new FormData();
      formData.append('description', description.trim());
      formData.append('diagnosticData', JSON.stringify(buildSafeIssueDiagnostic(sourceType, null, helpDocId)));
      formData.append('userAgent', navigator.userAgent);
      formData.append('screenSize', `${window.innerWidth}x${window.innerHeight}`);
      if (auth.kind === 'member') {
        formData.append('sourceType', sourceType);
        if (sourceType === 'AI_EDITOR_MEMBER' && sourceAccessId) {
          formData.append('sourceAccessId', sourceAccessId);
        } else {
          // Dashboard 신고만 현재 URL 을 남긴다. 작성 화면 URL 은 서버가 가린 형태로 고정한다.
          formData.append('currentUrl', currentUrl ?? window.location.href);
        }
      }
      screenshots.forEach((s, i) => {
        formData.append('screenshots', s.blob, `screenshot-${i}.png`);
      });
      files.forEach((f) => formData.append('files', f, f.name));

      await createIssueReport(auth, formData);

      toast({ title: '접수 완료', description: SUCCESS_BY_AUTH[auth.kind] });
      handleOpenChange(false);
    } catch (err) {
      if (err instanceof IssueReportRequestError && err.code === 'ISSUE_REPORTER_ACCOUNT_MISSING') {
        setFailure('신고자 계정을 확인하지 못해 접수할 수 없습니다. 작성 주소와 화면을 보관한 뒤 담당자에게 알려주세요.');
      } else if (err instanceof IssueReportRequestError && err.code === 'ISSUE_REPORT_RATE_LIMITED') {
        setFailure('짧은 시간에 여러 건이 접수되었습니다. 잠시 후 다시 시도해주세요.');
      } else if (err instanceof IssueReportRequestError && err.status === 401) {
        setFailure(
          auth.kind === 'member'
            ? '로그인이 만료되었습니다. 다시 로그인한 뒤 신고해주세요.'
            : '작성 링크 확인이 만료되었습니다. 화면을 새로 고친 뒤 다시 신고해주세요.',
        );
      } else {
        setFailure(err instanceof Error && err.message ? err.message : '접수하지 못했습니다. 잠시 후 다시 시도해주세요.');
      }
    } finally {
      setSubmitting(false);
    }
  }, [auth, description, hasContent, screenshots, files, sourceType, sourceAccessId, currentUrl, helpDocId, toast, handleOpenChange]);

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent
        ref={contentRef}
        data-issue-report-dialog="true"
        className="sm:max-w-[480px] max-h-[90dvh] overflow-y-auto"
        overlayClassName="bg-black/20"
        style={{ left: `calc(50% + ${offset.x}px)`, top: `calc(50% + ${offset.y}px)` }}
        onPaste={handlePaste}
      >
        <DialogHeader onPointerDown={startDrag} className="-m-2 cursor-move touch-none select-none rounded-md p-2"
          title="끌어서 옮길 수 있습니다">
          <DialogTitle>문제 신고</DialogTitle>
          <DialogDescription>
            내용을 적거나 화면·파일을 첨부해주세요. 화면 사진에는 보던 문서가 그대로 담깁니다. 운영팀이 확인한 뒤 답변합니다.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-2">
          <div>
            <Textarea
              aria-label="문제 내용"
              placeholder="어떤 문제가 발생했나요?"
              value={description}
              onChange={(e) => setDescription(e.target.value.slice(0, MAX_DESCRIPTION_LENGTH))}
              onKeyDown={(e) => {
                // Ctrl+Enter(맥은 ⌘+Enter)로 바로 접수한다(10-01 데모 R2). 한글 조합 중 Enter 는 넘긴다.
                if (e.key !== 'Enter' || !(e.ctrlKey || e.metaKey) || e.nativeEvent.isComposing) return;
                e.preventDefault();
                if (canSubmit) void handleSubmit();
              }}
              aria-keyshortcuts="Control+Enter"
              rows={4}
              className="resize-none"
            />
            <p className="text-xs text-muted-foreground mt-1 text-right">
              {description.length}/{MAX_DESCRIPTION_LENGTH}
            </p>
          </div>

          {/* 첨부 — 현재 화면은 사용자가 누를 때만 담는다 */}
          <div className="space-y-2">
            <div className="flex flex-wrap items-center gap-2">
              <Button
                type="button"
                variant="outline"
                size="sm"
                className="gap-1.5"
                onClick={() => void captureScreen()}
                disabled={capturing || screenshots.length >= MAX_SCREENSHOTS}
              >
                {capturing ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Camera className="h-3.5 w-3.5" />}
                현재 화면 첨부
              </Button>
              <Button
                type="button"
                variant="outline"
                size="sm"
                className="gap-1.5"
                onClick={() => fileInputRef.current?.click()}
                disabled={screenshots.length >= MAX_SCREENSHOTS}
              >
                <ImagePlus className="h-3.5 w-3.5" />
                사진 추가
              </Button>
              <Button
                type="button"
                variant="outline"
                size="sm"
                className="gap-1.5"
                onClick={() => docInputRef.current?.click()}
                title={`어떤 파일이든 ${MAX_FILE_MB}MB 까지 붙일 수 있습니다`}
              >
                <Paperclip className="h-3.5 w-3.5" />
                파일 첨부
              </Button>
              <span className="text-xs text-muted-foreground">
                첨부 {screenshots.length}/{MAX_SCREENSHOTS} · Ctrl+V 붙여넣기 가능
              </span>
            </div>
            {files.length > 0 ? (
              <ul className="space-y-1" aria-label="첨부 파일">
                {files.map((f, i) => (
                  <li key={`${f.name}-${i}`} className="flex items-center gap-2 rounded border px-2 py-1 text-xs">
                    <Paperclip className="h-3 w-3 shrink-0 text-muted-foreground" />
                    <span className="min-w-0 flex-1 truncate" title={f.name}>{f.name}</span>
                    <span className="shrink-0 text-muted-foreground">{formatSize(f.size)}</span>
                    <button
                      type="button"
                      aria-label={`${f.name} 첨부 제거`}
                      onClick={() => removeFile(i)}
                      className="shrink-0 rounded-full p-0.5 text-muted-foreground hover:bg-destructive/10 hover:text-destructive"
                    >
                      <X className="h-3 w-3" />
                    </button>
                  </li>
                ))}
              </ul>
            ) : null}
            {captureNote ? (
              <p role="status" className="text-xs text-muted-foreground">{captureNote}</p>
            ) : null}
            {screenshots.length > 0 ? (
              // 가로 스크롤 줄은 바깥으로 튀어나온 삭제 단추를 자른다 - 위·오른쪽에 그만큼 띄운다(10-04 신고 fdeb41d2).
              <div className="flex gap-2 overflow-x-auto pt-2 pr-2 pb-1">
                {screenshots.map((s, i) => (
                  <div key={s.url} className="relative shrink-0">
                    <Dialog>
                      <DialogTrigger asChild>
                        <button
                          type="button"
                          aria-label={`${s.label} 크게 보기`}
                          className="relative block cursor-zoom-in rounded focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                        >
                          <img src={s.url} alt={`${s.label} 미리보기`} className="h-20 w-auto rounded border object-cover" />
                          <span className="absolute bottom-0 left-0 right-0 bg-black/60 text-white text-[10px] text-center py-0.5 rounded-b">
                            {s.label}
                          </span>
                        </button>
                      </DialogTrigger>
                      <DialogContent
                        data-issue-report-dialog="true"
                        aria-describedby={undefined}
                        className="w-[calc(100vw-2rem)] max-w-6xl max-h-[90dvh] gap-3 p-4"
                      >
                        <DialogHeader>
                          <DialogTitle className="pr-8">{s.label} 크게 보기</DialogTitle>
                        </DialogHeader>
                        {/* 브라우저에서 생성한 Blob URL을 그대로 표시한다. */}
                        {/* eslint-disable-next-line @next/next/no-img-element */}
                        <img src={s.url} alt={`${s.label} 확대 이미지`} className="mx-auto max-h-[calc(90dvh-6rem)] max-w-full object-contain" />
                      </DialogContent>
                    </Dialog>
                    <button
                      type="button"
                      aria-label={`${s.label} 첨부 제거`}
                      onClick={() => removeScreenshot(i)}
                      className="absolute -top-1.5 -right-1.5 h-5 w-5 bg-destructive text-destructive-foreground rounded-full flex items-center justify-center"
                    >
                      <X className="h-3 w-3" />
                    </button>
                  </div>
                ))}
              </div>
            ) : null}
          </div>

          {/* 자동 수집 정보 — allowlist 만 */}
          <div className="rounded-md bg-muted p-3 text-xs space-y-1">
            <p className="font-medium text-foreground">자동 수집 정보</p>
            <p>
              <span className="text-muted-foreground">화면 종류:</span> {SOURCE_LABEL[sourceType]}
            </p>
            <p className="text-muted-foreground">이름·이메일, 보던 화면 주소, 브라우저 종류, 화면 크기, 발생 시각</p>
          </div>

          {failure ? (
            <p role="alert" className="rounded-md border border-destructive/40 bg-destructive/5 px-3 py-2 text-sm text-destructive">
              {failure}
            </p>
          ) : null}
        </div>

        {/* 숨겨진 파일 input (모바일: 카메라/갤러리 선택) */}
        <input
          ref={fileInputRef}
          type="file"
          accept="image/*"
          multiple
          className="hidden"
          onChange={handleFileSelect}
        />
        <input
          ref={docInputRef}
          type="file"
          multiple
          className="hidden"
          aria-label="첨부할 파일"
          onChange={handleDocSelect}
        />

        <DialogFooter className="gap-2 sm:items-start sm:gap-0">
          <Button type="button" variant="outline" onClick={() => handleOpenChange(false)} disabled={submitting}>
            취소
          </Button>
          <div className="flex flex-col">
            <Button type="button" onClick={() => void handleSubmit()} disabled={!canSubmit} aria-keyshortcuts="Control+Enter">
              {submitting && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              접수하기
            </Button>
            <span className="mt-1 flex items-center justify-center gap-0.5 text-[11px] text-muted-foreground/70" aria-hidden="true">
              Ctrl <CornerDownLeft className="h-3 w-3" />
            </span>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
